// ============================================================
// What each marker wears: notes, status, shape, scale, heading
// ============================================================
// `BattlefieldTokens` takes tokens, which are POSITIONS. Everything about
// the combatant standing there is flattened here into records keyed by id
// before it gets there. Nothing is stored: `projectDefender` is
// identity-cached on the combatant, so this costs a lookup per marker even
// mid-drag. Pure - `useMarkerDecorations` memoizes each on its inputs.

import { projectDefender } from '../../engine/roster/projection';
import { markerStatusOf } from '../../engine/battlefield/markerStatus';
import { resolveBodyType, isCrowd } from '../../engine/battlefield/tokenShape';
import { overlapAt, overlapLabel, isStuck } from '../../engine/battlefield/overlap';
import { movementUnder } from '../../engine/conditionMovement';
import { EMPTY, EMPTY_OVERLAPS, EMPTY_RECORD, EMPTY_STATUS } from './panelDefaults';
import { figureModel } from '../../engine/display/figureModel';
import { headPoint } from '../../engine/display/figures';
import { UP_M } from '../../engine/display/traitMarks';
import { verticalStop } from '../../engine/battlefield/reconcile';
import { surfaceUnder } from '../../engine/display/surfaces';
import { tokenFoot } from '../../engine/battlefield/tokens';
import { sameTeam, teamOf } from '../../engine/roster/team';
import { lightAt, lightingOf } from '../../engine/battlefield/lighting';
import { bodyMapLooks } from '../../engine/display/looks';
import { mapStatesOf } from '../../engine/display/states';
import { plateState } from '../../engine/plate/plateState';
import { isObject } from '../../engine/bodyShape';
import { objectState } from '../../engine/plate/objectPlate';
import { vitalsOf } from '../../engine/healthRamp';
import { withoutOwnBox } from '../../engine/battlefield/ownBox';
import { TOO_BIG_NOTE } from '../../engine/objects/objectSolids';
import { drawnDiameterOf } from '../../engine/objects/objectSpec';

/** The combatants who have a marker on this map, in roster order. */
function placedCombatants(roster, tokens) {
  return roster.filter((combatant) => tokens[combatant.id]);
}

/**
 * What each marker is standing inside, if anything.
 *
 * Normally nothing: a move follows the route and stops. But terrain can be
 * switched off, and a marker put anywhere STAYS there when it comes back
 * on - warned, not ejected. The warning is what makes that round trip
 * honest.
 */
export function overlapNotesFor(tokenList, solids, { ignoreTerrain, isGm, rulesFor, tooBig = [] }) {
  // An Object whose box the map cannot hold says so under its mark (2026-10-26), whatever else is true.
  const out = Object.fromEntries(tooBig.map((id) => [id, TOO_BIG_NOTE]));
  if (ignoreTerrain || !solids.length) return tooBig.length ? out : EMPTY_OVERLAPS;
  // Sight-only flag excluded here too (v8.199.4): ignoring LoS is not
  // permission to stand inside a wall.
  for (const token of tokenList.filter((t) => !rulesFor(t.id).ignoresTerrain)) {
    // An Object stands in its own box (2026-10-26): that is where it is, not a wall it is stuck in.
    const found = overlapAt({ x: token.x, y: token.elevation, z: token.z }, withoutOwnBox(solids, token.id), isGm);
    if (isStuck(found) || found.ground.length) out[token.id] = overlapLabel(found);
  }
  return out;
}

/**
 * WHAT IS WRONG WITH EVERY MARKER, not only the selected one.
 *
 * The reach and its explanation are drawn for one combatant - whoever is
 * the actor or the target - so an Immobilised vehicle nobody had selected
 * could be dragged about with nothing on screen to say it should not be
 * moving at all. The state belongs to the marker, so it is written on the
 * marker.
 */
export function conditionNotesFor(roster, tokens, { applyConditionModifiers, fatigueSlowsMovement }) {
  if (!applyConditionModifiers) return EMPTY_OVERLAPS;
  const out = {};
  // Not an Object (2026-10-26): it never moves, so a movement note means nothing - and "Dead" is not its word.
  for (const combatant of placedCombatants(roster, tokens).filter((c) => !isObject(c.sheet))) {
    const under = movementUnder(combatant.sheet?.movement, projectDefender(combatant).conditions ?? [], {
      fatigueSlows: fatigueSlowsMovement,
    });
    if (under.reasons.length) out[combatant.id] = under.stopped ? under.reasons[0] : under.reasons.join(' · ');
  }
  return out;
}

/**
 * What the ground under each marker is doing to them.
 *
 * Asked for explicitly: who is standing in what should be apparent from
 * the MAP, not only from a hover. It rides the marker-note channel that
 * already exists rather than adding a second one.
 */
export function surfaceNotesFor(surfaceEffects) {
  const out = {};
  for (const [id, effect] of Object.entries(surfaceEffects)) {
    const what = effect.labels.join(', ');
    const count = effect.grants.length;
    out[id] = count ? `${what} (${count} effect${count === 1 ? '' : 's'})` : what;
  }
  return out;
}

/** All the notes on one line, since a marker has room for one. */
export function joinMarkerNotes(overlaps, conditionNotes, surfaceNotes) {
  const ids = new Set([...Object.keys(overlaps), ...Object.keys(conditionNotes), ...Object.keys(surfaceNotes)]);
  if (!ids.size) return EMPTY_OVERLAPS;
  const out = {};
  for (const id of ids) {
    out[id] = [conditionNotes[id], surfaceNotes[id], overlaps[id]].filter(Boolean).join(' · ');
  }
  return out;
}

/**
 * WHAT EACH MARKER SAYS ABOUT THE PERSON STANDING THERE: the wound ring,
 * the condition chips, and - for the actor alone - what is left in the
 * magazine.
 */
export function markerStatusesFor(roster, tokens, { level, actorId, actorAmmo, maxChips }) {
  if (level === 'off') return EMPTY_STATUS;
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    const status = markerStatusOf(projectDefender(combatant), {
      level,
      maxChips,
      // Only the actor has a "current weapon" - `selectedSlot` belongs to
      // the attack panel, not to a combatant. So nobody else can be handed
      // a magazine, and the renderer needs no rule about it.
      ammo: combatant.id === actorId ? actorAmmo : null,
    });
    if (status) out[combatant.id] = status;
  }
  return out;
}

/**
 * Which combatants on the map are hordes, and how many are left.
 *
 * Read live off the roster's own projection, so grinding a horde down
 * shrinks it on the map with no second copy of the number anywhere.
 */
export function hordesOnMap(roster, tokens) {
  const out = [];
  for (const combatant of placedCombatants(roster, tokens)) {
    const token = tokens[combatant.id];
    const horde = projectDefender(combatant)?.horde;
    if (!horde?.isHorde || !(horde.magnitude > 0)) continue;
    // `y` is the storey they are standing on. It was assumed to be the
    // ground, so a horde on a gantry drew its area in the courtyard below
    // them (v8.39.0).
    out.push({
      id: token.id,
      x: token.x,
      z: token.z,
      y: token.elevation,
      magnitude: horde.magnitude,
      footprint: token.footprint,
    });
  }
  return out.length ? out : EMPTY;
}

/**
 * WHAT KIND OF THING EACH MARKER IS, for the shape it draws.
 *
 * Derived rather than stored on the Token: a token is written once, at
 * deploy, while body type is editable in the target panel the whole time
 * the map is open - a shape on the token would go stale the moment
 * somebody ticked "vehicle" (v8.39.0).
 */
export function bodyTypesFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens))
    out[combatant.id] = resolveBodyType(projectDefender(combatant));
  return out;
}

/**
 * A DRAWN-SIZE OVERRIDE, as a per-marker scale on the render radius.
 *
 * The bodyTypes pattern: derived per render from the live projection,
 * never stored on the Token - so editing "Token size (m)" mid-session
 * resizes the marker at once. Scale, not metres, because the renderer
 * multiplies rx = footprint * zoom; footprint itself is untouched (blast
 * inclusion, horde radii and heading geometry all read it).
 */
export function tokenScalesFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    const footprint = tokens[combatant.id].footprint;
    const d = drawnDiameterOf(projectDefender(combatant)?.tokenDiameter);
    if (d > 0 && footprint > 0) out[combatant.id] = d / 2 / footprint;
  }
  return out;
}

/**
 * WHICH OF THEM ARE CROWDS. Kept apart from the shape because "how many
 * of them" and "what kind of thing" are different questions - so a horde
 * of vehicles draws three hulls (v8.43.0).
 */
export function crowdsFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    if (isCrowd(projectDefender(combatant))) out[combatant.id] = true;
  }
  return out;
}

/**
 * WHAT EACH IS DRAWN AS on the display (plan map-display, B): class, posture,
 * movement trait, crowd, drawn size - from the projection and the sheet,
 * derived per render like the rest, never stored.
 */
export function figureModelsFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    out[combatant.id] = figureModel(projectDefender(combatant), combatant.sheet);
  }
  return out;
}

/**
 * WHAT EACH IS of its nature on the display (plan body-map M1): its looks, read off the projection and the sheet as the
 * plate reads them (the marker draws none once its figure lies dead; a beat still reads them). Only bodies with any;
 * derived per render, never stored.
 */
export function looksFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    const looks = bodyMapLooks(projectDefender(combatant), combatant.sheet ?? null);
    if (looks.length) out[combatant.id] = looks;
  }
  return out;
}

/** Which way each hull points. Read straight off the token. */
export function headingsOf(tokenList) {
  const out = {};
  for (const t of tokenList) if (t.heading !== undefined) out[t.id] = t.heading;
  return out;
}

/**
 * THE STANDING STATES each figure wears on the display (plan design-gaps, S2): its conditions read as the plate
 * reads them (engine/plate/plateState) and given their slots (engine/display/states). Every placed figure, a well
 * one with none: the onsets (hooks/useOnsets) count a figure missing from the record as never seen, so a well
 * figure's first state would never have its onset (the Track S review). Derived per render, never stored.
 */
export function statesFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens))
    out[combatant.id] = mapStatesOf(plateState(projectDefender(combatant).conditions ?? []));
  return out;
}

/** Each figure's posture as the one thing it wears (S4), for the posture's onset (hooks/useOnsets). */
export function posturesOf(models) {
  const out = {};
  for (const [id, model] of Object.entries(models)) out[id] = [{ id: model.posture }];
  return out;
}

/**
 * WHAT EACH VEHICLE'S FACING RING READS on the display (plan design-gaps, V1): its armour by facing, where the actor
 * stands (none when it is the actor), and the table's arc preset. The tokens layer draws the ring round the hull where
 * it is drawn this frame (engine/display/facingRing). Only vehicles; derived per render, never stored.
 */
export function vehicleFacingsFor(roster, tokens, actorId, preset) {
  const out = {};
  const shooter = actorId ? (tokens[actorId] ?? null) : null;
  for (const combatant of placedCombatants(roster, tokens)) {
    const target = projectDefender(combatant);
    if (resolveBodyType(target) !== 'vehicle') continue;
    out[combatant.id] = { ...hullFacing(target), shooter: combatant.id === actorId ? null : shooter, preset };
  }
  return out;
}

/** A hull's armour by facing, and its facing choice (a GM's pin wins, as it does for the armour the attack meets). */
function hullFacing(target) {
  const { front = 0, side = 0, rear = 0 } = target.armour;
  return { armour: { front, side, rear }, choice: target.vehicleFacing ?? null };
}

/**
 * EACH HORDE'S COUNT on the display (plan design-gaps, V3): its magnitude and its whole, for a body per point and its
 * magnitude arc. Only crowds; derived per render, never stored.
 */
export function hordesFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    const target = projectDefender(combatant);
    if (isCrowd(target)) out[combatant.id] = { magnitude: target.horde.magnitude, max: target.horde.maxMagnitude };
  }
  return out;
}

/**
 * EACH OBJECT'S HEALTH on the display (2026-10-26, board 5): its state (intact, damaged, critical, destroyed) and its
 * Wounds, for the ring that is its gauge. Only objects; derived per render, never stored.
 */
export function objectsFor(roster, tokens) {
  const out = {};
  for (const combatant of placedCombatants(roster, tokens)) {
    if (!isObject(combatant.sheet)) continue;
    const target = projectDefender(combatant);
    out[combatant.id] = { state: objectState(target), ...vitalsOf(target) };
  }
  return out;
}

/**
 * WHAT A FIGURE'S TRAIT READS OF THE MAP (plan design-gaps, T1), by token id, only for figures with either: the height
 * of what a raised figure stands on (`under`: the plumb line and its height), and whether a ghost is inside a wall
 * (`inside`: its tag, and a ripple where it goes in or comes out). Read off the DRAWN tokens, so a ghost walking
 * through a wall ripples as it passes.
 */
export function traitGroundsFor(tokenList, models, solids, groundAt) {
  const out = {};
  for (const token of tokenList) {
    const model = models[token.id];
    if (!model) continue;
    // Keyed on the trait, not the model: the models are rebuilt on every roster echo, the trait seldom changes.
    const ground = perToken(GROUNDS, token, [model.trait, solids, groundAt], () => ({
      ...underOf(token, solids, groundAt),
      ...insideOf(token, model, solids),
    }));
    if (Object.keys(ground).length) out[token.id] = ground;
  }
  return out;
}

/**
 * One answer per token OBJECT and the inputs it read (the Track T review): the drawn list is rebuilt every frame a
 * figure walks, but every figure standing still keeps its object - so only the walkers are asked again.
 */
function perToken(cache, token, inputs, compute) {
  const hit = cache.get(token);
  if (hit && hit.inputs.every((v, i) => v === inputs[i])) return hit.value;
  const value = compute();
  cache.set(token, { inputs, value });
  return value;
}
const GROUNDS = new WeakMap();
const STEPS = new WeakMap();

/** What a figure stands on, when it is up off the floor: the top of the highest thing under it, else the floor. */
function underOf(token, solids, groundAt) {
  const floor = groundAt(token.x, token.z);
  if (token.elevation - floor <= UP_M) return {};
  const under = verticalStop(token.x, token.z, token.elevation, floor, solids, { floor });
  // Standing on a crate is standing: only a figure up off what is under it has a ground to tell.
  return token.elevation - under > UP_M ? { under } : {};
}

/** Whether an incorporeal figure's middle is inside something solid. */
function insideOf(token, model, solids) {
  if (model.trait !== 'incorporeal') return {};
  return { inside: overlapAt(headPoint(token), solids).blocking.length > 0 };
}

/** Each ghost's side of the wall as a state (in or out), so useOnsets gives a ripple at each crossing. */
export function wallSidesOf(grounds) {
  const out = {};
  for (const [id, g] of Object.entries(grounds))
    if (g.inside !== undefined) out[id] = [{ id: g.inside ? 'in' : 'out' }];
  return out;
}

/**
 * WHAT EACH FIGURE STANDS IN (plan design-gaps, T3): its surface by label - or `ground` - as a state, so useOnsets
 * gives the moment it steps into one (the entry beat). Read off the DRAWN tokens, so a walker's entry plays where it
 * steps in, not where the move was committed; the figures with a model alone (the display's).
 */
export function surfaceStepsFor(tokenList, models, surfaces) {
  const out = {};
  for (const token of tokenList) {
    if (!models[token.id]) continue;
    out[token.id] = perToken(STEPS, token, [surfaces], () => {
      const under = surfaceUnder(tokenFoot(token), surfaces);
      return [{ id: under?.label ?? 'ground', colour: under?.colour ?? null }];
    });
  }
  return out;
}

/** The entries in onset: each figure that just stepped into a surface, with the surface's colour (not onto ground). */
export function surfaceEntriesOf(onsets, steps) {
  const out = {};
  for (const [id, gained] of Object.entries(onsets)) {
    const now = steps[id]?.[0];
    if (now && now.id !== 'ground' && gained.includes(now.id)) out[id] = { label: now.id, colour: now.colour };
  }
  return out;
}

/**
 * THE OTHER SIDE, for the heat under it (plan design-gaps, T4): everyone on a team other than the acting figure's.
 * None when the actor has no team, nor anyone on none (the map cannot say whose side they are on).
 */
export function otherSideOf(roster, actorId) {
  const actor = roster.find((c) => c.id === actorId);
  if (!teamOf(actor)) return EMPTY_RECORD;
  const out = {};
  for (const c of roster) if (c.id !== actorId && teamOf(c) && !sameTeam(c, actor)) out[c.id] = true;
  return out;
}

/**
 * WHO STANDS IN THE DARK (plan design-gaps, Z2; the stealth page's "a blip in the dark is dim"), by token id: none in
 * daylight; under darkness everyone; with lit areas, whoever stands outside them. Cached per token object, so only
 * the walkers are asked again each frame.
 */
export function inTheDarkFor(tokenList, terrain) {
  if (lightingOf(terrain) === 'daylight') return EMPTY_RECORD;
  const out = {};
  for (const t of tokenList)
    if (perToken(DARK, t, [terrain], () => lightAt(terrain, t.x, t.z) === 'dark')) out[t.id] = true;
  return out;
}
const DARK = new WeakMap();
